import PropTypes from 'prop-types';
import { useEffect, useState, useRef, useMemo } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import dayjs from 'dayjs';

import { getHipLinkToken, linkCareContext } from 'utils/apiHelper';
import { openSnackbar } from 'api/snackbar';

// ABDM HIP linking is a two-step flow:
//   1. `getHipLinkToken` — advertise a patient's ABHA address to the gateway
//      and request a link token. The patient will see a notification in
//      their PHR app to authorize the link.
//   2. `linkCareContext` — once (or immediately after, depending on backend
//      contract) the token is issued, register the specific care contexts
//      (e.g. an OP consultation, a discharge summary) that belong to that
//      patient and should be pullable via consent later.
//
// The dialog is generic over `careContexts` so any caller (appointment row,
// lab report row, IPD discharge, etc.) can pre-seed the list. First entry
// point wired is the OPD appointment row in AppointmentListBase.

// ABDM canonical HI types the gateway will accept. Kept in sync with
// `AbhaConsentManager` — if you edit this list, update that too.
const HI_TYPE_OPTIONS = [
  'OPConsultation',
  'Prescription',
  'DischargeSummary',
  'DiagnosticReport',
  'ImmunizationRecord',
  'HealthDocumentRecord',
  'WellnessRecord',
  'Invoice'
];

const normaliseGender = (raw) => {
  if (!raw) return undefined;
  const s = String(raw).trim().toLowerCase();
  if (s === 'm' || s === 'male') return 'M';
  if (s === 'f' || s === 'female') return 'F';
  return 'O';
};

// Backend rejects string/ISO DOB with "dob must be an integer" — must be
// year as bare int. If the patient record has a full ISO date we send the
// year portion; if it's already a number we pass it through.
const extractDobYear = (dob) => {
  if (dob == null || dob === '') return undefined;
  if (typeof dob === 'number') return dob;
  const parsed = dayjs(dob);
  if (parsed.isValid()) return parsed.year();
  const asNum = Number(dob);
  return Number.isFinite(asNum) ? asNum : undefined;
};

// Backend rejects string ABHA number with "abha_number must be an integer".
// Strip hyphens / spaces from the human-readable "12-3456-7890-1234" form
// and coerce to a Number. Returns undefined if the value can't become an int.
const normaliseAbhaNumber = (raw) => {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  const digits = String(raw).replace(/\D+/g, '');
  if (!digits) return undefined;
  const asNum = Number(digits);
  return Number.isFinite(asNum) ? asNum : undefined;
};

const STEPS = ['Get Link Token', 'Link Care Contexts', 'Done'];

const AbhaHipLinker = ({ open, handleClose, patient, careContexts = [], onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [linkToken, setLinkToken] = useState(null);
  const [gettingToken, setGettingToken] = useState(false);
  const [tokenPolling, setTokenPolling] = useState(false);
  const [errorBanner, setErrorBanner] = useState(null);
  const pollTimerRef = useRef(null);
  const maxRetriesRef = useRef(0);
  
  useEffect(() => {
    if (!open) {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      setTokenPolling(false);
      setGettingToken(false);
    }
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [open]);
  const [linking, setLinking] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [hiType, setHiType] = useState('OPConsultation');

  // Reset everything each time the dialog is (re)opened so the flow is
  // deterministic across successive openings for different appointments.
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setLinkToken(null);
      setGettingToken(false);
      setLinking(false);
      setSelectedIds(careContexts.map((c) => c.referenceNumber));
      const firstType = careContexts[0]?.hiType;
      setHiType(firstType && HI_TYPE_OPTIONS.includes(firstType) ? firstType : 'OPConsultation');
    }
  }, [open, careContexts]);

  const effectivePatient = useMemo(() => {
    if (!patient) return {};
    const nested = patient.patient_details || patient.patient || patient.user || {};
    return {
      ...nested,
      ...patient,
      abha_address:
        patient.abha_address ||
        nested.abha_address ||
        patient.abhaAddress ||
        nested.abhaAddress ||
        patient.health_id ||
        nested.health_id ||
        patient.healthId ||
        nested.healthId ||
        patient.abha_id ||
        nested.abha_id ||
        '',
      abha_number:
        patient.abha_number ||
        nested.abha_number ||
        patient.abhaNumber ||
        nested.abhaNumber ||
        '',
      full_name:
        patient.full_name ||
        nested.full_name ||
        patient.name ||
        nested.name ||
        '',
      gender:
        patient.gender ||
        nested.gender ||
        '',
      dob:
        patient.dob ||
        nested.dob ||
        patient.date_of_birth ||
        nested.date_of_birth ||
        patient.year_of_birth ||
        nested.year_of_birth ||
        '',
      id:
        patient.id ||
        nested.id ||
        patient.patient_id ||
        nested.patient_id ||
        ''
    };
  }, [patient]);

  const hasAbha = Boolean(effectivePatient.abha_address);
  const abhaNumberInt = normaliseAbhaNumber(effectivePatient.abha_number);
  const hasAbhaNumber = Number.isFinite(abhaNumberInt);

  const pollForToken = async (payload) => {
    if (maxRetriesRef.current >= 15) {
      setTokenPolling(false);
      setGettingToken(false);
      setErrorBanner('ABDM gateway took too long to respond. Please verify your callback configuration or try again.');
      return;
    }

    try {
      const { data, status } = await getHipLinkToken(payload);
      
      if (status === 200) {
        setTokenPolling(false);
        setGettingToken(false);
        setLinkToken(data?.linkToken || data?.link_token || 'issued');
        setActiveStep(1);
        openSnackbar({
          open: true,
          message: 'Link token issued. Patient should approve the notification in their PHR app.',
          variant: 'alert',
          alert: { color: 'success' }
        });
      } else if (status === 202) {
        maxRetriesRef.current += 1;
        pollTimerRef.current = setTimeout(() => pollForToken(payload), 3000);
      }
    } catch (error) {
      setTokenPolling(false);
      setGettingToken(false);
      console.error('HIP get_link_token failed during polling', error);
      let errMsg = 'Failed to fetch link token from ABDM gateway.';
      const respErr = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      if (respErr) {
        if (typeof respErr === 'string') {
          errMsg = respErr;
        } else if (typeof respErr === 'object' && respErr.message) {
          errMsg = respErr.message;
        } else {
          errMsg = JSON.stringify(respErr);
        }
      }
      setErrorBanner(errMsg);
    }
  };

  const handleGetToken = async () => {
    if (!hasAbha) {
      openSnackbar({
        open: true,
        message: 'Patient does not have an ABHA address on file.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      return;
    }
    if (!hasAbhaNumber) {
      openSnackbar({
        open: true,
        message: 'Patient is missing a 14-digit ABHA number. Complete ABHA enrollment (M1) first.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      return;
    }
    
    const dobYear = extractDobYear(effectivePatient.dob);
    if (!dobYear) {
      openSnackbar({
        open: true,
        message: 'Patient is missing a Date of Birth (Year). Please update the patient profile, as it is required by ABDM for linking.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      return;
    }

    setGettingToken(true);
    setTokenPolling(true);
    maxRetriesRef.current = 0;
    setErrorBanner(null);

    const payload = {
      abha_address: effectivePatient.abha_address,
      abha_number: abhaNumberInt,
      full_name: effectivePatient.full_name,
      gender: normaliseGender(effectivePatient.gender),
      dob: extractDobYear(effectivePatient.dob)
    };

    try {
      const { data, status } = await getHipLinkToken(payload);
      
      if (status === 200) {
        setTokenPolling(false);
        setGettingToken(false);
        setLinkToken(data?.linkToken || data?.link_token || 'issued');
        setActiveStep(1);
        openSnackbar({
          open: true,
          message: 'Link token issued. Patient should approve the notification in their PHR app.',
          variant: 'alert',
          alert: { color: 'success' }
        });
      } else if (status === 202) {
        maxRetriesRef.current += 1;
        pollTimerRef.current = setTimeout(() => pollForToken(payload), 3000);
      } else {
        setTokenPolling(false);
        setGettingToken(false);
      }
    } catch (error) {
      setTokenPolling(false);
      setGettingToken(false);
      console.error('HIP get_link_token failed', error);
      let errMsg = 'Failed to fetch link token from ABDM gateway.';
      const respErr = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      if (respErr) {
        if (typeof respErr === 'string') {
          errMsg = respErr;
        } else if (typeof respErr === 'object' && respErr.message) {
          errMsg = respErr.message;
        } else {
          errMsg = JSON.stringify(respErr);
        }
      }
      setErrorBanner(errMsg);
    }
  };

  const handleLinkContexts = async () => {
    const chosen = careContexts.filter((c) => selectedIds.includes(c.referenceNumber));
    if (chosen.length === 0) {
      openSnackbar({
        open: true,
        message: 'Select at least one care context to link.',
        variant: 'alert',
        alert: { color: 'warning' }
      });
      return;
    }
    setLinking(true);
    try {
      const abhaNumberStr = effectivePatient.abha_number != null ? String(effectivePatient.abha_number).replace(/-/g, '') : '';
      
        await linkCareContext({
          abhaAddress: effectivePatient.abha_address,
          abhaNumber: abhaNumberStr,
          patient: [
            {
              referenceNumber: String(effectivePatient.id),
              display: effectivePatient.full_name || 'Patient',
              careContexts: chosen.map((c) => ({
                referenceNumber: String(c.referenceNumber),
                display: c.display
              })),
              hiType: hiType,
              count: chosen.length
            }
          ]
        });
      setActiveStep(2);
      if (onSuccess) {
        onSuccess(chosen.map((c) => String(c.referenceNumber)));
      }
      openSnackbar({
        open: true,
        message: `Linked ${chosen.length} care context${chosen.length === 1 ? '' : 's'} to ${effectivePatient.abha_address}.`,
        variant: 'alert',
        alert: { color: 'success' }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('HIP link_care_context failed', error);
      openSnackbar({
        open: true,
        message: error?.response?.data?.error || 'Failed to link care contexts.',
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setLinking(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Link Care Contexts to ABHA</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1">
              {effectivePatient.full_name || 'Patient'}
            </Typography>
            {hasAbha ? (
              <Chip
                icon={<LinkIcon />}
                label={effectivePatient.abha_address}
                color="primary"
                variant="outlined"
                size="small"
              />
            ) : (
              <Chip label="No ABHA address on file" color="warning" size="small" />
            )}
          </Box>

          {!hasAbha && (
            <Alert severity="warning">
              This patient does not have an ABHA address. Enroll or verify the patient&apos;s
              ABHA in the M1 flow before linking care contexts.
            </Alert>
          )}

          {errorBanner && (
            <Alert severity="error" onClose={() => setErrorBanner(null)}>
              {errorBanner}
            </Alert>
          )}

          {hasAbha && !hasAbhaNumber && (
            <Alert severity="warning">
              Patient has an ABHA address but no 14-digit ABHA number on file. The gateway
              requires both — complete the M1 verification to fetch the number.
            </Alert>
          )}

          <Stepper activeStep={activeStep} sx={{ py: 1 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Step 1 — request a link token from the ABDM gateway. The patient will
                receive an authorization notification in their PHR app.
              </Typography>
              <Button
                variant="contained"
                onClick={handleGetToken}
                disabled={!hasAbha || !hasAbhaNumber || gettingToken}
                startIcon={gettingToken ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
              >
                {tokenPolling ? 'Awaiting ABDM gateway...' : gettingToken ? 'Requesting…' : 'Get Link Token'}
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Step 2 — pick the care contexts to publish. Only the boxes below are
                linked; unchecked rows stay private.
              </Typography>

              {careContexts.length === 0 ? (
                <Alert severity="info">
                  No care contexts were passed to the dialog. This is a wiring bug —
                  the opening component should pass at least one context.
                </Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Reference</TableCell>
                      <TableCell>Display</TableCell>
                      <TableCell>HI Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {careContexts.map((c) => (
                      <TableRow key={c.referenceNumber} hover>
                        <TableCell padding="checkbox">
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                checked={selectedIds.includes(c.referenceNumber)}
                                onChange={() => toggleSelected(c.referenceNumber)}
                              />
                            }
                            label=""
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {c.referenceNumber}
                        </TableCell>
                        <TableCell>{c.display}</TableCell>
                        <TableCell>
                          <Chip label={c.hiType || hiType} size="small" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}

          {activeStep === 2 && (
            <Alert
              icon={<CheckCircleIcon fontSize="inherit" />}
              severity="success"
            >
              Care contexts linked to {patient?.abha_address || 'the patient'}. They will now surface
              when the patient consents to share records from your facility.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          {activeStep === 2 ? 'Close' : 'Cancel'}
        </Button>
        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={handleLinkContexts}
            disabled={linking || !linkToken || selectedIds.length === 0}
            startIcon={linking ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {linking ? 'Linking…' : `Link ${selectedIds.length} Selected`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

AbhaHipLinker.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    full_name: PropTypes.string,
    abha_address: PropTypes.string,
    abha_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    gender: PropTypes.string,
    dob: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  careContexts: PropTypes.arrayOf(
    PropTypes.shape({
      referenceNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      display: PropTypes.string.isRequired,
      hiType: PropTypes.string
    })
  )
};

export default AbhaHipLinker;
